/**
 * longeva-collection-filter.js
 *
 * Longeva Aesthetics collection filter enhancements.
 * Layers Longeva UX on top of Horizon's native facets system.
 *
 * Responsibilities:
 *   1. Inject an applied-filter chip row ABOVE the product grid.
 *      Horizon renders the chip pills inside the sidebar form; this
 *      duplicates them into a separate element above the grid so they
 *      are visible without scrolling into the sidebar.
 *
 *   2. Keep an ARIA live region announcing result counts when filters change.
 *
 *   3. Manage sticky-sidebar top-offset when the site header height changes
 *      (e.g. on scroll or breakpoint change).
 *
 *   4. Handle focus management: when the mobile filter drawer closes, return
 *      focus to the "Filter" trigger button.
 *
 *   5. Chip remove animation: add .is-removing class before navigation so
 *      the 150ms opacity/scale exit plays before the page reload.
 *
 * This file does NOT:
 *   - Override window.fetch or any global browser API.
 *   - Modify DOM inside a MutationObserver without disconnecting first.
 *   - Duplicate Horizon's AJAX facet logic (Horizon handles filter AJAX
 *     natively via facets-form-component + results-list web components).
 *   - Contain any pharmacy/regulatory content.
 *
 * No em dashes. British English. No !important.
 */

(function longevaCollectionFilter() {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* 0. Guard: only run on PLP pages                                     */
  /* ------------------------------------------------------------------ */
  if (!document.documentElement.classList.contains('longeva-plp-page')) return;

  /* ------------------------------------------------------------------ */
  /* 1. Constants                                                        */
  /* ------------------------------------------------------------------ */
  const CHIP_ROW_ID       = 'longeva-filter-chips';
  const ANNOUNCE_ID       = 'longeva-filter-announce';
  const CHIP_CLASS        = 'longeva-chip';
  const CHIPS_WRAPPER     = 'longeva-filter-chips';
  const REMOVE_CLASS      = 'is-removing';
  const SIDEBAR_TOP_TOKEN = '--longeva-filter-sticky-top';

  /* SVG X icon for chip remove buttons (inline, no external fetch) */
  const CLOSE_SVG = `<svg viewBox="0 0 10 10" fill="none" aria-hidden="true" focusable="false">
    <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`;

  /* ------------------------------------------------------------------ */
  /* 2. Utility: find the main-collection-grid element                   */
  /* ------------------------------------------------------------------ */
  function getGrid() {
    return document.querySelector('.main-collection-grid');
  }

  /* ------------------------------------------------------------------ */
  /* 3. Utility: read active filters from Horizon's sidebar chip source  */
  /* ------------------------------------------------------------------ */
  /**
   * Reads the active filter chips that Horizon's filter-remove-buttons
   * snippet rendered inside the sidebar/drawer.
   *
   * Returns an array of { label, url } objects suitable for building
   * the above-grid chip row.
   *
   * Horizon renders facet-remove-component elements with:
   *   - data-url: the URL that removes this filter
   *   - text content: the filter label
   *   - A visually-hidden "Remove" span (we exclude from label)
   *
   * We read from the desktop facets form, not the drawer, to avoid
   * double-counting.
   */
  function readActiveFilterChips() {
    const sidebarPills = document.querySelectorAll(
      '.facets-block-wrapper--vertical facet-remove-component.facets-remove__pill'
    );

    if (!sidebarPills.length) return [];

    return Array.from(sidebarPills).map(function (pill) {
      const url   = pill.getAttribute('data-url') || '';
      const label = getVisibleText(pill);
      return { label: label, url: url };
    }).filter(function (chip) { return chip.label && chip.url; });
  }

  /**
   * Extract visible (non-visually-hidden) text from an element.
   * Excludes .visually-hidden spans.
   */
  function getVisibleText(el) {
    let text = '';
    el.childNodes.forEach(function (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList && node.classList.contains('visually-hidden')) return;
        if (node.tagName === 'SPAN' && node.classList.contains('svg-wrapper')) return;
        text += getVisibleText(node);
      }
    });
    return text.trim();
  }

  /* ------------------------------------------------------------------ */
  /* 4. Utility: read current result count from Horizon's products-count */
  /* ------------------------------------------------------------------ */
  function readResultCount() {
    const countEl = document.querySelector(
      '.facets-block-wrapper--vertical .products-count-wrapper [role="status"]'
    ) || document.querySelector(
      '.facets--horizontal .products-count-wrapper [role="status"]'
    ) || document.querySelector(
      '.products-count-wrapper [role="status"]'
    );
    return countEl ? countEl.textContent.trim() : '';
  }

  /* ------------------------------------------------------------------ */
  /* 5. Build / update the above-grid chip row                          */
  /* ------------------------------------------------------------------ */
  function buildChipRow() {
    const grid   = getGrid();
    if (!grid) return;

    const chips  = readActiveFilterChips();
    let chipRow  = document.getElementById(CHIP_ROW_ID);

    if (!chips.length) {
      /* No active filters: remove the row if it exists */
      if (chipRow) chipRow.remove();
      return;
    }

    /* Create or clear the row */
    if (!chipRow) {
      chipRow = document.createElement('div');
      chipRow.id    = CHIP_ROW_ID;
      chipRow.className = CHIPS_WRAPPER;
      /* ARIA: announce as a navigation region for active filters */
      chipRow.setAttribute('role', 'navigation');
      chipRow.setAttribute('aria-label', 'Active filters');
      /* Insert before the first child of the grid */
      grid.insertBefore(chipRow, grid.firstChild);
    }

    /* We rebuild chip row contents on every update.
       Disconnect observer before modifying DOM; reconnect after. */
    if (chipObserver) chipObserver.disconnect();

    chipRow.innerHTML = '';

    chips.forEach(function (chip) {
      const a = document.createElement('a');
      a.className = CHIP_CLASS;
      a.href      = chip.url;
      a.setAttribute('aria-label', 'Remove filter: ' + chip.label);

      const labelSpan = document.createElement('span');
      labelSpan.textContent = chip.label;
      a.appendChild(labelSpan);

      const removeSpan = document.createElement('span');
      removeSpan.className = CHIP_CLASS + '__remove';
      removeSpan.setAttribute('aria-hidden', 'true');
      removeSpan.innerHTML = CLOSE_SVG;
      a.appendChild(removeSpan);

      /* Chip remove animation: add .is-removing before navigation */
      a.addEventListener('click', function (e) {
        e.preventDefault();
        const href = a.href;
        a.classList.add(REMOVE_CLASS);
        /* 150ms matches the CSS transition duration */
        setTimeout(function () { window.location.href = href; }, 150);
      });

      /* Keyboard: Enter/Space fires the click handler */
      a.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          a.click();
        }
      });

      chipRow.appendChild(a);
    });

    /* Append "Clear all" link at the end */
    const clearUrl = readClearAllUrl();
    if (clearUrl) {
      const clearBtn = document.createElement('a');
      clearBtn.className = 'longeva-chips-clear';
      clearBtn.href      = clearUrl;
      clearBtn.textContent = 'Clear all';
      clearBtn.setAttribute('aria-label', 'Clear all active filters');
      chipRow.appendChild(clearBtn);
    }

    /* Reconnect observer after DOM modifications are complete */
    startChipObserver();
  }

  /**
   * Read the "clear all" URL from Horizon's clear-all button in the sidebar.
   * Horizon renders this as .facets__clear-all-link in the vertical form.
   */
  function readClearAllUrl() {
    const clearBtn = document.querySelector(
      '.facets-block-wrapper--vertical .facets__clear-all-link'
    );
    if (clearBtn) {
      /* facet-remove-component sets data-url on the parent */
      const parent = clearBtn.closest('[data-url]');
      if (parent) return parent.getAttribute('data-url');
    }
    /* Fallback: strip all filter params from current URL */
    const url = new URL(window.location.href);
    const keysToRemove = [];
    url.searchParams.forEach(function (val, key) {
      if (key.startsWith('filter.')) keysToRemove.push(key);
    });
    keysToRemove.forEach(function (k) { url.searchParams.delete(k); });
    return url.toString();
  }

  /* ------------------------------------------------------------------ */
  /* 6. ARIA live region for result count announcements                  */
  /* ------------------------------------------------------------------ */
  function ensureAnnouncer() {
    if (document.getElementById(ANNOUNCE_ID)) return;
    const announcer = document.createElement('div');
    announcer.id              = ANNOUNCE_ID;
    announcer.className       = 'longeva-filter-count-announce';
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.setAttribute('role', 'status');
    document.body.appendChild(announcer);
  }

  function announceResultCount() {
    const announcer = document.getElementById(ANNOUNCE_ID);
    if (!announcer) return;
    const count = readResultCount();
    if (!count) return;
    /* Clear and re-set to force screen-reader re-announcement */
    announcer.textContent = '';
    /* rAF ensures the DOM diff is committed before the new text */
    requestAnimationFrame(function () {
      announcer.textContent = count;
    });
  }

  /* ------------------------------------------------------------------ */
  /* 7. Sticky sidebar: measure header height + set CSS token            */
  /* ------------------------------------------------------------------ */
  function updateStickyTop() {
    /* Horizon uses a sticky <header> / <sticky-header> element.
       We measure it and expose the height as a CSS variable on :root
       so the sidebar's `top: var(--longeva-filter-sticky-top)` stays
       in sync at all viewport widths. */
    const header = document.querySelector('sticky-header, header[id]') ||
                   document.querySelector('header');
    const offset = header ? header.getBoundingClientRect().height + 8 : 80;
    document.documentElement.style.setProperty(
      SIDEBAR_TOP_TOKEN,
      Math.round(offset) + 'px'
    );
  }

  /* ------------------------------------------------------------------ */
  /* 8. Mobile drawer focus management                                   */
  /* ------------------------------------------------------------------ */
  function initDrawerFocus() {
    const drawerEl = document.getElementById('filters-drawer');
    const trigger  = document.querySelector(
      'button[on\\:click="#filters-drawer/showDialog"]'
    ) || document.querySelector('.facets-toggle__button');
    if (!drawerEl || !trigger) return;

    /* Horizon uses dialog-component which fires native dialog 'close' event.
       When the drawer closes, return focus to the trigger button. */
    drawerEl.addEventListener('close', function () {
      /* Small delay so Horizon's own close animation can complete */
      setTimeout(function () {
        if (trigger && document.body.contains(trigger)) {
          trigger.focus({ preventScroll: true });
        }
      }, 80);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 9. MutationObserver: keep chip row in sync with sidebar chips       */
  /* ------------------------------------------------------------------ */
  let chipObserver = null;

  function startChipObserver() {
    const sidebarFacets = document.querySelector(
      '.facets-block-wrapper--vertical .facets-remove'
    ) || document.querySelector('.facets__form-wrapper');

    if (!sidebarFacets) return;

    if (chipObserver) chipObserver.disconnect();

    chipObserver = new MutationObserver(function () {
      /* Disconnect before any DOM modifications to prevent loops.
         buildChipRow() will reconnect after it finishes. */
      chipObserver.disconnect();
      buildChipRow();
      announceResultCount();
    });

    chipObserver.observe(sidebarFacets, {
      childList: true,
      subtree:   true,
      attributes: true,
      attributeFilter: ['data-url', 'class']
    });
  }

  /* ------------------------------------------------------------------ */
  /* 10. Horizon AJAX result update hook                                 */
  /* ------------------------------------------------------------------ */
  /**
   * Horizon's results-list web component dispatches a 'results-loaded'
   * event when the product grid is re-rendered after a filter change.
   * We listen for this to rebuild our chip row and announce the count.
   */
  function initResultsLoadedListener() {
    document.addEventListener('results-loaded', function () {
      buildChipRow();
      announceResultCount();
    });

    /* Also listen for Horizon's facets-updated custom event as a fallback */
    document.addEventListener('facets-updated', function () {
      buildChipRow();
      announceResultCount();
    });
  }

  /* ------------------------------------------------------------------ */
  /* 11. Header resize observer: update sticky offset                    */
  /* ------------------------------------------------------------------ */
  function initHeaderObserver() {
    const header = document.querySelector('sticky-header, header[id]') ||
                   document.querySelector('header');
    if (!header) return;

    if (typeof ResizeObserver === 'undefined') {
      /* Fallback: update on scroll only */
      window.addEventListener('scroll', updateStickyTop, { passive: true });
      return;
    }

    const ro = new ResizeObserver(updateStickyTop);
    ro.observe(header);
  }

  /* ------------------------------------------------------------------ */
  /* 12. Init                                                            */
  /* ------------------------------------------------------------------ */
  function init() {
    ensureAnnouncer();
    updateStickyTop();
    buildChipRow();
    announceResultCount();
    startChipObserver();
    initResultsLoadedListener();
    initDrawerFocus();
    initHeaderObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    /* DOM already ready (module scripts execute after parsing) */
    init();
  }
})();
